import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useToast } from '@/hooks/useToast'
import { usePrintMethod } from '@/hooks/useGeneralSettings'
import { extractApiError } from '@/lib/api/client'
import {
  flattenLabelUrls,
  labelsToPreview,
  printApi,
  resolveAgentId,
  type KMPrintItem,
  type LabelsResponse,
  type PrintJob,
  type PrintRequest,
} from '@/lib/api/print'
import { printLabelsViaBrowser, type BrowserPrintPayload } from '@/lib/browserPrint'
import { startAndPollLabels, type LabelPrepareStart } from '@/lib/labelPreparePoll'
import { pollKmRenderUntilDone } from '@/lib/kmRenderPoll'
import type { LabelPrepareProgress } from '@/lib/api/print'
import { useAuthStore } from '@/stores/auth.store'
import { beginRenderHistory, finishRenderHistory, useRenderHistoryStore } from '@/stores/renderHistory.store'

const POLL_MS = 1500
const TIMEOUT_MS = 120_000

async function waitForPrintJob(jobId: string, signal: AbortSignal): Promise<PrintJob> {
  const started = Date.now()
  while (Date.now() - started < TIMEOUT_MS) {
    if (signal.aborted) throw new Error('Отменено')
    const res = await printApi.getJob(jobId)
    const job = res.data.item
    if (job.status === 'completed') return job
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw new Error(job.error_message?.trim() || 'Печать не удалась')
    }
    await new Promise((r) => setTimeout(r, POLL_MS))
  }
  throw new Error('Печать не завершилась за отведённое время')
}

type PrintSubmit = (agentId: string) => Promise<{ data: { item: PrintJob } }>

async function submitAndWait(submit: PrintSubmit, signal: AbortSignal) {
  const agentId = await resolveAgentId({ localOnly: true })
  const res = await submit(agentId)
  const jobId = res.data.item?.id
  if (jobId) {
    await waitForPrintJob(jobId, signal)
  }
  return res
}

function payloadFromLabels(title: string, item: LabelsResponse, force?: boolean): BrowserPrintPayload {
  return {
    title,
    labels: labelsToPreview(item),
    markTargets: item.mark_targets ?? [],
    force,
  }
}

export function usePrintActions(taskUuid: string) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const printMethod = usePrintMethod()
  const isAdmin = useAuthStore((s) => s.role === 'admin')
  const kmRendering = useRenderHistoryStore((s) =>
    s.active.some((a) => a.source === 'task_km' && a.taskUuid === taskUuid),
  )
  const [browserPreview, setBrowserPreview] = useState<BrowserPrintPayload | null>(null)
  const [isFetchingLabels, setIsFetchingLabels] = useState(false)
  const [isBrowserPrinting, setIsBrowserPrinting] = useState(false)
  const [fetchProgress, setFetchProgress] = useState<LabelPrepareProgress | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['task', taskUuid] })
    qc.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
    qc.invalidateQueries({ queryKey: ['task-pallets', taskUuid] })
    qc.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
  }

  const run = useMutation({
    mutationFn: (submit: PrintSubmit) => submitAndWait(submit, new AbortController().signal),
    onSuccess: () => {
      toast.success('Напечатано')
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const recordPrintInBackground = useCallback(
    (payload: BrowserPrintPayload) => {
      if (payload.markTargets.length === 0) {
        invalidate()
        return
      }
      void printApi
        .recordPrint(taskUuid, {
          mark_targets: payload.markTargets,
          force: payload.force,
        })
        .then(() => invalidate())
        .catch((err) => toast.error(extractApiError(err)))
    },
    [invalidate, taskUuid, toast],
  )

  const confirmBrowserPrint = useCallback(async () => {
    if (!browserPreview || isBrowserPrinting) return
    const payload = browserPreview
    setIsBrowserPrinting(true)
    try {
      const urls = payload.labels.map((l) => l.url)
      await printLabelsViaBrowser(urls)
      setBrowserPreview(null)
      toast.success('Напечатано')
      recordPrintInBackground(payload)
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsBrowserPrinting(false)
    }
  }, [browserPreview, isBrowserPrinting, recordPrintInBackground, toast])

  const renderKm = useMutation({
    mutationFn: async (trackId: string) => {
      try {
        const postRes = await printApi.renderAllKm(taskUuid)
        const session = postRes.data.item
        if (session.status === 'completed') {
          finishRenderHistory(
            trackId,
            'ok',
            `${session.rendered_count ?? 0} КМ, ${session.boxes_count ?? 0} коробок`,
            { taskUuid },
          )
          return { data: { item: session } }
        }
        if (session.status === 'failed') {
          const msg = session.error?.trim() || 'ошибка рендера'
          finishRenderHistory(trackId, 'error', msg, { taskUuid })
          throw new Error(msg)
        }
        const done = await pollKmRenderUntilDone(taskUuid, trackId)
        return { data: { item: done } }
      } catch (err) {
        finishRenderHistory(trackId, 'error', extractApiError(err), { taskUuid })
        throw err
      }
    },
    onSuccess: (res) => {
      const item = res.data.item
      if (item.status === 'completed' || item.rendered_count != null) {
        toast.success(`Отрендерено ${item.rendered_count ?? 0} КМ в ${item.boxes_count ?? 0} коробках`)
      }
    },
    onError: (err) => toast.error(extractApiError(err)),
    onSettled: (_data, err, trackId) => {
      const active = useRenderHistoryStore.getState().active
      const stillActive = active.some((a) => a.id === trackId)
      if (stillActive && err) {
        finishRenderHistory(trackId, 'error', extractApiError(err), { taskUuid })
      }
    },
  })

  const base = (extra?: Partial<PrintRequest>) => ({
    force: isAdmin || (extra?.force ?? false),
    copies: extra?.copies ?? 1,
  })

  const openBrowserPreview = useCallback(
    async (title: string, startPrepare: LabelPrepareStart, expectedCount?: number) => {
      setIsFetchingLabels(true)
      setFetchProgress({ percent: 0, done: 0, total: expectedCount ?? 0 })
      setBrowserPreview({ title, labels: [], markTargets: [], expectedCount })
      try {
        const item = await startAndPollLabels(startPrepare, setFetchProgress)
        const urls = flattenLabelUrls(item)
        if (!urls.length) {
          setBrowserPreview(null)
          toast.error('Нечего печатать')
          return
        }
        setBrowserPreview(payloadFromLabels(title, item, isAdmin))
      } catch (err) {
        setBrowserPreview(null)
        toast.error(extractApiError(err))
      } finally {
        setIsFetchingLabels(false)
        setFetchProgress(null)
      }
    },
    [isAdmin, toast],
  )

  const dispatchPrint = useCallback(
    (title: string, agentSubmit: PrintSubmit, startPrepare: LabelPrepareStart, expectedCount?: number) => {
      if (printMethod === 'browser') {
        void openBrowserPreview(title, startPrepare, expectedCount)
        return
      }
      run.mutate(agentSubmit)
    },
    [openBrowserPreview, printMethod, run],
  )

  return {
    isPrinting: printMethod === 'browser' ? false : run.isPending,
    isBrowserPrinting,
    isRenderingKm: renderKm.isPending || kmRendering,
    browserPreview,
    isFetchingLabels,
    fetchProgress,
    closeBrowserPreview: () => setBrowserPreview(null),
    confirmBrowserPrint: () => void confirmBrowserPrint(),
    renderAllKm: () => {
      if (kmRendering || renderKm.isPending) return
      const trackId = beginRenderHistory('task_km', `Задание ${taskUuid}`, { taskUuid })
      renderKm.mutate(trackId)
    },
    printBox: (boxNum: number, opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КА — коробка ${boxNum}`,
        (agentId) => printApi.printBox(taskUuid, boxNum, { agent_id: agentId, ...base(opts) }),
        () => printApi.boxLabelsPrepare(taskUuid, boxNum, { ka_only: true, ...base(opts) }),
      ),
    printBoxBundle: (boxNum: number, opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КМ+КА — коробка ${boxNum}`,
        (agentId) => printApi.printBoxBundle(taskUuid, boxNum, { agent_id: agentId, ...base(opts) }),
        () => printApi.boxLabelsPrepare(taskUuid, boxNum, base(opts)),
      ),
    printBoxKa: (boxNum: number, opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КА — коробка ${boxNum}`,
        (agentId) => printApi.printBox(taskUuid, boxNum, { agent_id: agentId, ...base(opts) }),
        () => printApi.boxLabelsPrepare(taskUuid, boxNum, { ka_only: true, ...base(opts) }),
      ),
    printPallet: (palletNum: number, opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КА — паллета ${palletNum}`,
        (agentId) => printApi.printPallet(taskUuid, palletNum, { agent_id: agentId, ...base(opts) }),
        () => printApi.palletLabelsPrepare(taskUuid, palletNum, base(opts)),
        1,
      ),
    printPalletBoxes: (palletNum: number, opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КА коробок — паллета ${palletNum}`,
        (agentId) => printApi.printPalletBoxes(taskUuid, palletNum, { agent_id: agentId, ...base(opts) }),
        () => printApi.palletBoxesLabelsPrepare(taskUuid, palletNum, base(opts)),
      ),
    printKm: (sequenceNumbers: number[], opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КМ (${sequenceNumbers.length})`,
        (agentId) =>
          printApi.printKm(taskUuid, { agent_id: agentId, sequence_numbers: sequenceNumbers, ...base(opts) }),
        () =>
          printApi.kmLabelsPrepare(taskUuid, {
            sequence_numbers: sequenceNumbers,
            force: base(opts).force,
          }),
        sequenceNumbers.length,
      ),
    printKmItems: (items: KMPrintItem[], opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        `Печать КМ (${items.length})`,
        (agentId) => printApi.printKm(taskUuid, { agent_id: agentId, items, ...base(opts) }),
        () => printApi.kmLabelsPrepare(taskUuid, { items, force: base(opts).force }),
        items.length,
      ),
    printKmAll: (opts?: Partial<PrintRequest>) =>
      dispatchPrint(
        'Печать всех КМ',
        (agentId) => printApi.printKmAll(taskUuid, { agent_id: agentId, ...base(opts) }),
        () => printApi.kmAllLabelsPrepare(taskUuid, base(opts)),
      ),
  }
}
